// src/components/ui/Deportistas.jsx
import { motion, useAnimationControls } from "framer-motion";
import { useEffect, useMemo, useState, useCallback } from "react";
import useDeportistas from "../../hooks/useDeportistas";

const ROTATE_MS = 7000;

const Deportistas = () => {
  const { deportistas, loading } = useDeportistas();

  // 2 por vista en desktop, 1 en móvil
  const [perPage, setPerPage] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? 1 : 2
  );
  const [page, setPage] = useState(0);

  // Animación SIN desmontar el DOM (evita re-cargas visibles)
  const controls = useAnimationControls();

  useEffect(() => {
    const onResize = () => {
      const next = window.innerWidth < 768 ? 1 : 2;
      setPerPage(next);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Cantidad de páginas
  const pageCount = useMemo(() => {
    const n = deportistas?.length || 0;
    if (!n) return 0;
    return Math.ceil(n / perPage);
  }, [deportistas, perPage]);

  // Si cambia perPage o data, corrige page si se sale
  useEffect(() => {
    if (!pageCount) return;
    setPage((p) => (p >= pageCount ? 0 : p));
  }, [pageCount]);

  const goNext = useCallback(() => {
    if (!pageCount) return;
    setPage((p) => (p + 1) % pageCount);
  }, [pageCount]);

  const goPrev = useCallback(() => {
    if (!pageCount) return;
    setPage((p) => (p - 1 + pageCount) % pageCount);
  }, [pageCount]);

  // Auto-rotación
  useEffect(() => {
    if (!pageCount) return;
    const id = setTimeout(goNext, ROTATE_MS);
    return () => clearTimeout(id);
  }, [page, pageCount, goNext]);

  // Preload (cache temporal) de imágenes
  useEffect(() => {
    if (!deportistas?.length) return;

    // Si son pocas, precarga todas y listo (cero parpadeo)
    deportistas.forEach((d) => {
      if (!d?.imagenURL) return;
      const img = new Image();
      img.src = d.imagenURL;
    });
  }, [deportistas]);

  // Animación suave al cambiar page/perPage (sin remount)
  useEffect(() => {
    (async () => {
      controls.set({ opacity: 0, y: 10 });
      await controls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.35, ease: "easeOut" },
      });
    })();
  }, [page, perPage, controls]);

  // Items visibles (wrap) para siempre rellenar 2 (o 1 en móvil)
  const visible = useMemo(() => {
    const list = deportistas || [];
    const n = list.length;
    if (!n) return [];
    const start = page * perPage;

    return Array.from({ length: perPage }).map((_, i) => {
      const idx = (start + i) % n;
      return list[idx];
    });
  }, [deportistas, page, perPage]);

  if (loading) {
    return (
      <section className="text-center bg-[#E5E5E5] py-16">
        <p className="text-sm text-gray-400">Cargando deportistas…</p>
      </section>
    );
  }

  if (!deportistas?.length) return null;

  return (
    <section
      className="
        relative
        left-1/2 right-1/2
        -ml-[50vw] -mr-[50vw]
        w-screen
        py-28
        bg-[#E5E5E5]
        overflow-hidden
      "
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16">
        <h2 className="text-2xl md:text-2xl lg:text-3xl font-bold text-[#00698F] text-center">
          Nuestros deportistas
        </h2>

        <p className="mt-5 text-base md:text-lg lg:text-xl text-[#00698F] text-center max-w-xl mx-auto">
          Atletas que confían en EKINE para su preparación y recuperación
        </p>

        {/* CARRUSEL */}
        <div className="mt-14 relative">
          {/* Flechas laterales — SOLO DESKTOP */}
          <button
            type="button"
            onClick={goPrev}
            aria-label="Deportistas anteriores"
            className="
              hidden md:flex
              absolute top-1/2 -translate-y-1/2 -left-12 
              w-10 h-10 rounded-full bg-white/80 backdrop-blur-xl 
              border border-white/70 shadow-lg items-center 
              justify-center text-[#0A1440] hover:bg-white transition
              z-20
            "
          >
            ‹
          </button>

          <button
            type="button"
            onClick={goNext}
            aria-label="Siguientes deportistas"
            className="
              hidden md:flex
              absolute top-1/2 -translate-y-1/2 -right-12 
              w-10 h-10 rounded-full bg-white/80 backdrop-blur-xl 
              border border-white/70 shadow-lg items-center 
              justify-center text-[#0A1440] hover:bg-white transition
              z-20
            "
          >
            ›
          </button>

          {/* Vista actual (2 en desktop, 1 en móvil) */}
          <motion.div
            initial={false}
            animate={controls}
            className={`
              grid gap-10
              ${perPage === 1 ? "grid-cols-1" : "grid-cols-2"}
              justify-items-center
              py-10
            `}
          >
            {visible.map((d, i) => (
              <motion.div
                key={`${d.id}-${i}`}
                className="text-center origin-center"
                whileHover={{ scale: 1.06 }}
                transition={{ type: "spring", stiffness: 180, damping: 18 }}
              >
                <div className="w-80 h-[26rem] rounded-2xl overflow-hidden shadow-xl bg-white">
                  <img
                    src={d.imagenURL}
                    alt={d.nombre}
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                  />
                </div>

                <div className="mt-6">
                  <p className="font-semibold text-[#0A1440]">{d.nombre}</p>
                  {d.disciplina && (
                    <span className="text-sm text-gray-500">{d.disciplina}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Flechas inferiores — SOLO MÓVIL */}
          <div className="flex md:hidden items-center justify-center gap-6 mt-2">
            <button
              onClick={goPrev}
              aria-label="Deportistas anteriores"
              className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-xl border border-white/70 shadow-lg flex items-center justify-center text-[#0A1440]"
            >
              ‹
            </button>

            <button
              onClick={goNext}
              aria-label="Siguientes deportistas"
              className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-xl border border-white/70 shadow-lg flex items-center justify-center text-[#0A1440]"
            >
              ›
            </button>
          </div>

          {/* Indicador tipo pastillas */}
          {pageCount > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              {Array.from({ length: pageCount }).map((_, i) => (
                <button
                  key={i}
                  aria-label={`Ir a página ${i + 1}`}
                  onClick={() => setPage(i)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    i === page
                      ? "w-8 bg-[#3A6AFF]"
                      : "w-3 bg-[#C7D7FF] hover:bg-[#A9C0FF]"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Deportistas;
